import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    // 에디터 전역 컨텍스트 설정
    window.currentProjectId = this.element.dataset.projectId
    window.currentUserId = this.element.dataset.userId

    // UI기획은 category를 'planning'으로 고정
    window.selectedCategory = 'planning'

    // 갓 생성된 빈 프로젝트는 항상 UI 탭으로 시작 (localStorage 기록 무시)
    const isFreshProject = this.element.dataset.projectFresh === 'true'
    if (isFreshProject) {
      localStorage.setItem('selectedChatMode', 'ui_only')
    }

    const savedMode = localStorage.getItem('selectedChatMode')
    const savedAiMode = localStorage.getItem('selectedAiMode')

    if (savedMode) {
      window.selectedMode = savedMode
      const radio = document.querySelector(`input[name="chat-mode"][value="${savedMode}"]`)
      if (radio) radio.checked = true
    }

    if (savedAiMode) {
      window.selectedAiMode = savedAiMode
      const label = document.getElementById('ai-mode-label')
      if (label) label.textContent = savedAiMode.toUpperCase()
    }

    // 프로젝트명 입력 이벤트
    this._setupProjectNameInput()

    // 에디터 모듈 로드 (한 번만)
    if (!window.__uiPlanningLoaded) {
      window.__uiPlanningLoaded = true
      this._loadEditorModules()
    }
  }

  disconnect() {
    window.__uiPlanningLoaded = false
  }

  _setupProjectNameInput() {
    const input = document.getElementById('project-name-input')
    if (!input) return

    // B1/B2: 마지막으로 서버에 저장된 이름을 초기값으로 보관
    this._lastSavedProjectName = input.value
    this._renameInFlight = false

    input.addEventListener('blur', async () => {
      // 동시 blur 진입 방지
      if (this._renameInFlight) return
      if (!window.currentProjectId) return

      const newName = input.value.trim()

      // 빈 이름 → 이전 값으로 복원, 저장 생략
      if (!newName) {
        input.value = this._lastSavedProjectName
        return
      }

      // 이름 변화 없음 → 불필요한 PATCH 생략
      if (newName === this._lastSavedProjectName) return

      this._renameInFlight = true

      try {
        // B1: window.ProjectManager가 아직 없으면 최대 5초 폴링
        const pm = await this._waitForProjectManager(5000)

        if (!pm) {
          // 5초 안에 모듈 로드 안 됨 → 복원 + 오류 피드백
          input.value = this._lastSavedProjectName
          this._showRenameError(input)
          return
        }

        let ok = false
        try {
          const result = await pm.updateProjectName(window.currentProjectId, newName)
          // updateProjectName이 falsy 또는 명시적 false를 반환하면 실패로 간주
          ok = result !== false && result !== null && result !== undefined ? true : !!result
          // 반환값이 없는(undefined) 경우도 성공으로 처리 (throw 없이 반환 == 성공 관례)
          if (result === undefined) ok = true
        } catch (e) {
          console.error('[UiPlanningEditor] 프로젝트명 업데이트 오류:', e)
          ok = false
        }

        if (ok) {
          this._lastSavedProjectName = newName
          document.title = `${newName} - UI기획`
        } else {
          input.value = this._lastSavedProjectName
          this._showRenameError(input)
        }
      } finally {
        this._renameInFlight = false
      }
    })

    input.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault()
        this.blur()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault()
        this.select()
      }
    })
  }

  // window.ProjectManager가 등장할 때까지 최대 timeoutMs 동안 100ms 간격으로 대기
  _waitForProjectManager(timeoutMs) {
    return new Promise((resolve) => {
      if (window.ProjectManager && typeof window.ProjectManager.updateProjectName === 'function') {
        resolve(window.ProjectManager)
        return
      }
      const interval = 100
      let elapsed = 0
      const timer = setInterval(() => {
        elapsed += interval
        if (window.ProjectManager && typeof window.ProjectManager.updateProjectName === 'function') {
          clearInterval(timer)
          resolve(window.ProjectManager)
        } else if (elapsed >= timeoutMs) {
          clearInterval(timer)
          resolve(null)
        }
      }, interval)
    })
  }

  // 프로젝트명 저장 실패 시 입력창 근처에 토스트 표시 (3초 후 자동 제거)
  // #save-status 를 사용하지 않는 이유: SaveButtonHandler가 해당 요소의 innerHTML·data-state를
  // 자동 저장 라이프사이클과 함께 지속적으로 덮어쓰기 때문에 경쟁 상태가 발생한다.
  // 독립된 토스트 요소를 input 옆에 삽입하면 두 시스템이 완전히 분리된다.
  _showRenameError(input) {
    const TOAST_ID = 'uip-rename-error-toast'

    // 단일 인스턴스 보장: 이미 표시 중이면 제거 후 재생성
    const existing = document.getElementById(TOAST_ID)
    if (existing) existing.remove()

    const toast = document.createElement('div')
    toast.id = TOAST_ID
    toast.setAttribute('role', 'alert')
    toast.setAttribute('aria-live', 'assertive')
    toast.textContent = '프로젝트 이름 저장에 실패했습니다'
    Object.assign(toast.style, {
      position: 'absolute',
      top: '100%',
      left: '0',
      marginTop: '4px',
      padding: '6px 12px',
      background: '#111827',
      color: '#ffffff',
      fontSize: '12px',
      fontWeight: '500',
      borderRadius: '0',
      whiteSpace: 'nowrap',
      zIndex: '9999',
      pointerEvents: 'none'
    })

    // input의 부모를 기준 컨테이너로 활용 (position: relative 보장)
    const container = input.parentElement
    const prevPosition = container.style.position
    if (!prevPosition || prevPosition === 'static') {
      container.style.position = 'relative'
    }
    container.appendChild(toast)

    setTimeout(() => {
      toast.remove()
      // position을 임시로 바꿨다면 복원
      if (!prevPosition || prevPosition === 'static') {
        container.style.position = prevPosition || ''
      }
    }, 3000)
  }

  async _loadEditorModules() {
    // application.js 번들이 esbuild 최소화 과정에서 Marked.js 클래스를 var $ = class {} 로 이름 붙여
    // window.$를 덮어쓴다. jQuery는 window.jQuery로도 등록되므로 import 직전에 복구.
    if (typeof window.jQuery !== 'undefined') {
      window.$ = window.jQuery
    }
    try {
      // index.js가 진입점: ChatManager, ProjectManager, AI클라이언트 등 모두 import 후 UIController 인스턴스 생성
      await import('/js/ui-planning/index.js?v=' + Date.now())
    } catch (e) {
      console.error('[UiPlanningEditor] 에디터 모듈 로드 실패:', e)
    }
  }
}
